import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import type { ApolloError } from 'apollo-client';

import { token } from '@atlaskit/tokens';

import { ReactionsProvider } from '@confluence/comment-context';

import { Comments } from '../Comments';
import type { FeedContentQuery_feed_nodes_summaryLineUpdate_FeedEventComment as SummaryLineCommentUpdate } from '../../__types__/FeedContentQuery';
import type { RenderedContentOptions } from '../RenderedContent';
import { RenderedContent } from '../RenderedContent';

import { FeedCardTitle } from './FeedCardTitle';
import { FeedCardByLine } from './FeedCardByLine';
import { FeedCardActionCounts, ShowCommentState } from './FeedCardActionCounts';
import { FeedCardActions } from './FeedCardActions';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FollowingCardBody = styled.div({
	borderTop: `1px solid ${token('color.border', '#f0f0f3')}`,
	paddingTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopularCardBody = styled.div({
	flex: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentExcerpt = styled.div({
	maxHeight: '40px',
	marginBottom: token('space.150', '12px'),
	display: '-webkit-box',
	WebkitLineClamp: '2',
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	wordBreak: 'break-word',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageDetails = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndActionsWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	marginBottom: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndBylineWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageDetailsContentWrapper = styled.div({
	minWidth: 0,
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentsWrapper = styled.div({
	marginTop: token('space.150', '12px'),
	paddingTop: token('space.150', '12px'),
	paddingBottom: 0,
	borderTop: `1px solid ${token('color.border', '#f0f0f3')}`,
});

const getShowCommentState = (
	shouldHideComments?: boolean,
	comment?: SummaryLineCommentUpdate,
	commentVisible?: boolean,
): ShowCommentState => {
	if (!shouldHideComments) {
		return ShowCommentState.Number;
	}
	if (!!comment) {
		return commentVisible ? ShowCommentState.HideComment : ShowCommentState.ShowComment;
	}
	return ShowCommentState.NoShow;
};

type FeedCardBodyProps = {
	toggleActionMenu: (open: boolean) => void;

	contentId: string;
	contentTitle: string;
	contentType: string;
	contentSubType?: string | null;
	contentUrl: string;
	spaceId?: string;
	spaceName?: string;
	spaceUrl?: string;
	authorName?: string;
	authorId?: string;
	ownerName?: string;
	ownerId?: string;
	emoji?: string;
	customIconUrl?: string;
	contentExcerpt?: string;

	numLikes: number;
	handleLikeClick: () => void;
	likeMutationError?: ApolloError | undefined;
	userLiked: boolean;
	numComments: number;
	hideSocialProof?: boolean;

	comment?: SummaryLineCommentUpdate;
	renderedContent?: RenderedContentOptions;
	popularCardLayout?: boolean;
	timestamp?: string;
	hideActions?: boolean;

	hasBeenHovered: boolean;
	lastModifiedDate?: string;
	source: string;
	shouldHideComments?: boolean;
};

export const FeedCardBody: FC<FeedCardBodyProps> = ({
	toggleActionMenu,
	contentId,
	contentTitle,
	contentType,
	contentSubType,
	contentUrl,
	spaceId,
	spaceName,
	spaceUrl,
	authorName,
	authorId,
	ownerName,
	ownerId,
	emoji,
	customIconUrl,
	contentExcerpt,
	numLikes,
	handleLikeClick,
	likeMutationError,
	userLiked,
	numComments,
	hideSocialProof,
	comment,
	renderedContent,
	popularCardLayout = false,
	timestamp,
	hideActions,
	hasBeenHovered,
	lastModifiedDate,
	source,
	shouldHideComments,
}) => {
	const CardBody = popularCardLayout ? PopularCardBody : FollowingCardBody;
	// check if page owner exists and content is not a blog
	// checking against the name here for the case of anonymous users
	const shouldUsePageOwner = ownerName && contentType === 'page';

	const [commentVisible, setCommentVisible] = useState(false);

	const onCommentToggleClick = useCallback(() => {
		setCommentVisible(!commentVisible);
	}, [commentVisible]);

	const showCommentState: ShowCommentState = getShowCommentState(
		shouldHideComments,
		comment,
		commentVisible,
	);

	return (
		<ReactionsProvider contentId={contentId}>
			<CardBody>
				<PageDetails>
					<PageDetailsContentWrapper>
						<TitleAndActionsWrapper>
							<TitleAndBylineWrapper>
								<FeedCardTitle
									contentTitle={contentTitle}
									contentUrl={contentUrl}
									contentType={contentType}
									emoji={emoji}
									customIconUrl={customIconUrl}
								/>
								{shouldUsePageOwner ? (
									<FeedCardByLine
										spaceName={spaceName}
										spaceUrl={spaceUrl}
										ownerName={ownerName}
										ownerId={ownerId}
										timestamp={timestamp}
									/>
								) : (
									<FeedCardByLine
										spaceName={spaceName}
										spaceUrl={spaceUrl}
										authorName={authorName}
										authorId={authorId}
										timestamp={timestamp}
									/>
								)}
							</TitleAndBylineWrapper>
							{!hideActions && (
								<FeedCardActions
									contentUrl={contentUrl}
									contentId={contentId}
									contentTitle={contentTitle}
									contentType={contentType as 'page' | 'blogpost'}
									contentSubType={contentSubType}
									setIsActionMenuOpen={toggleActionMenu}
									hasCardBeenHovered={hasBeenHovered}
									source={source}
									lastModifiedDate={lastModifiedDate || ''}
								/>
							)}
						</TitleAndActionsWrapper>

						{contentExcerpt && <ContentExcerpt>{contentExcerpt}</ContentExcerpt>}
						{!hideSocialProof && (
							<FeedCardActionCounts
								numLikes={numLikes}
								userLiked={userLiked}
								handleLikeClick={handleLikeClick}
								numComments={numComments}
								likeMutationError={likeMutationError}
								contentId={contentId}
								contentType={contentType}
								spaceId={spaceId}
								showCommentState={showCommentState}
								onCommentToggleClick={onCommentToggleClick}
							/>
						)}
					</PageDetailsContentWrapper>
				</PageDetails>

				{comment && (!shouldHideComments || commentVisible) && (
					<CommentsWrapper>
						<Comments comments={[comment]} pageId={contentId} contentType={contentType} />
					</CommentsWrapper>
				)}

				{renderedContent && <RenderedContent content={renderedContent} />}
			</CardBody>
		</ReactionsProvider>
	);
};
